/*
  standards: https://www.w3.org/TR/webvtt1/

  auto_start_transcription = true
  https://docs.daily.co/reference/rest-api/transcript
      newCallObject.stopTranscription() or startTranscription()

*/

import React, {
  DragEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { IconButton, styled, Button } from '@mui/material';
import Box from '@mui/material/Box';

import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';

import {
  useDaily,
  useThrottledDailyEvent,
  useTranscription,
} from '@daily-co/daily-react';

import { useBookingsContext } from '../../contexts/BookingsContext';
import { useCopilotContext } from '../../contexts/CopilotContext';
import { useSessionContext } from '../../contexts/SessionContext';

import {
  ITranscriptSegment,
  ITranscriptSentence,
  IImage,
  INote,
} from '../../global/interfaces';

import { BorderColor, PostAdd } from '@mui/icons-material';

import { axiosRequest } from '../../utils/axiosRequest';
import { scrollToBottom, renderOps } from '../../utils/helpers';

import { HPSwitch } from '../SwitchToggle';
import TranscriptNote from '../TranscriptNote';
import EditBox from './EditBox';

import { useTranscriptStyles } from './styles';
import { StyledSideTabs, StyledSideTab } from '../StyledTabs';
import IncludeOptions from './IncludeOptions';
import UploadDialog from './UploadDialog';
import ConfirmResetDialog from './ConfirmResetDialog';
import { IDelta } from '@homeporter/types';
import useSession from '../../hooks/useSession';
import ImageBlock from './ImageBlock';
import AnalyzeDialog from './AnalyzeDialog';
import toggleToast from '../../utils/messaging';
import { useAIDocumentEditorContext } from '../../contexts/AIDocumentEditorContext';
import { de } from 'date-fns/locale';
import theme from '../../theme';
const EditorWrapper = styled(Box)`
  .add {
    background-color: #c8ffc8;
  }

  .del {
    /*display: none;*/
    color: #f00;
    text-decoration: line-through;
    opacity: 0.7;
  }
`;

interface TranscriptProps {
  useTabs: boolean;
  viewTitle: string;
  setRegenerateIndex?: (index: number | undefined) => void;
}

const StyledSentence = styled('span')<{
  hoverEnabled: boolean;
  color: string | undefined;
  backgroundColor: string | undefined;
}>(({ hoverEnabled, color, backgroundColor }) => ({
  paddingRight: '.5rem',
  color: color,
  backgroundColor: backgroundColor,
  transition: 'background-color 0.3s',
  '&:hover': {
    cursor: hoverEnabled ? 'pointer' : 'default',
    backgroundColor: hoverEnabled ? '#f9f5cc' : 'inherit',
    color: hoverEnabled ? '#000' : 'inherit',
  },
}));

const Transcript = ({
  viewTitle,
  useTabs,
  setRegenerateIndex,
}: TranscriptProps) => {
  const styles = useTranscriptStyles();
  const { selectedBooking, profile, userSettings } = useBookingsContext();
  const {
    booking,
    sipSessionId,
    setSessionTranscript,
    imagesMapRef,
    transcriptSegments,
    resetSessionTranscript,
    transcribeState,
    setTranscribeState,
    setSelectedImage,
    transcriptAttachments,
    remoteParticipant,
  } = useSessionContext();

  const { downloadSessionData } = useSession();

  const {
    searchEnqueue,
    aiMode,
    scoreThreshold,
    copilotEnabled,
    selectedSentenceId,
    filterTranscriptResultsMap,
    classifyTranscriptResultsMapRef,
    filterTranscriptPrompt,
    selectedClassifierOptions,
  } = useCopilotContext();

  const {
    highlightedSids,
    setHighlightedSids,
    highlightSentence,
    scrollTo,
    selectedModel,
    setSelectedModel,
    isGeneratingResponse,
  } = useAIDocumentEditorContext();

  const scrollableElRef = useRef<any>();
  const callObject = useDaily();

  const [hideImages, setHideImages] = useState<boolean>(false);
  const [hideNotes, setHideNotes] = useState<boolean>(false);
  const [hideAIResults, setHideAIResults] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(0);
  const handleChangeTab = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue || 0);
  };

  const [allCollapsed, setAllCollapsed] = useState<boolean>(false);

  const toggleCollapseAll = () => {
    setAllCollapsed((prev) => !prev);
  };

  useEffect(() => {
    if (transcribeState === 'start' && !isTranscribing) {
      console.log('starting transcribe...');
      callObject?.startTranscription();
      setTranscribeState('starting');
    }

    if (transcribeState === 'starting' && isTranscribing) {
      setTranscribeState('on');
    }

    if (transcribeState === 'stop' && isTranscribing) {
      console.log('stopping transcribe...');
      callObject?.stopTranscription();
      setTranscribeState('stopping');
    }
  }, [transcribeState]);

  const { isTranscribing } = useTranscription();
  useThrottledDailyEvent(
    ['transcription-stopped', 'transcription-started', 'transcription-error'],
    useCallback((events: any) => {
      events.forEach((event: any) => {
        console.log('::::useThrottledDailyEvent:', event);

        switch (event.action) {
          case 'transcription-started':
            console.log('::::transcription-started');
            setTranscribeState('on');
            break;

          case 'transcription-stopped':
            setTranscribeState('off');
            break;

          case 'transcription-error':
            setTranscribeState('error');
            break;
        }
      });
    }, []),
  );

  useEffect(() => {
    if (
      (!!remoteParticipant || !!sipSessionId) &&
      !isTranscribing &&
      transcribeState != 'starting'
    )
      setTranscribeState('start');
  }, [remoteParticipant, sipSessionId]);
  //<<<<<<<<<<<<<<<<<<<<<<<<<<

  // <<<<<<<<<<<<<<<<<<< CO-PIOLOT >>>>>>>>>>>>>>>>>>>
  const handleClickSentence = (e: MouseEvent<HTMLSpanElement>) => {
    console.log(':::handleClickSentence...');
    e.stopPropagation();
    const element = e.target as HTMLElement;

    const sentence: ITranscriptSentence = { sid: '', text: '' };
    if (!element.id) {
      for (let i = 0; i < element.children.length; ++i)
        sentence.text +=
          (sentence.text === '' ? '' : ' ') +
          (element.children[i] as HTMLElement).innerText;
    } else {
      sentence.sid = element.id;
      sentence.text = element.innerText;
    }

    searchEnqueue(sentence);
  };

  const [hideFiltedText, setHideFiltedText] = useState<boolean>(false);
  const toggleHideFiltedText = () => {
    setHideFiltedText((prev) => !prev);
  };

  // <<<<<<<<<<<<<<<<<<< UPLOAD & RESET TRANSCRIPT >>>>>>>>>>>>>>>>>>>
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [transcriptFile, setTranscriptFile] = useState(null);

  const handleFileChange = (event: any) => {
    setTranscriptFile(event.target.files[0]);
  };

  const handleUpload = async (event: any) => {
    event.preventDefault();
    if (!transcriptFile) {
      alert('Please select a file to upload');
      return;
    }

    const formData = new FormData();
    formData.append('transcript', transcriptFile);
    if (!booking) return;

    try {
      const axios = axiosRequest();
      const response = await axios.post(
        `/sessions/upload_transcript/${booking?.booking_id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      if (response.data.session_transcripts) {
        setSessionTranscript(booking, response.data.session_transcripts);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }
    setOpenUploadModal(false);
  };

  const handleResetTranscript = async () => {
    if (!booking) return;

    const axios = axiosRequest();
    const result = await axios.post(
      `/sessions/reset_transcript/${booking?.booking_id}`,
    );

    if (result?.data?.status === 'success') {
      resetSessionTranscript();
    }
  };

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const handleConfirmReset = () => {
    setOpenConfirmModal(true);
  };

  const handleCloseConfirmModal = () => {
    setOpenConfirmModal(false);
  };

  const handleConfirmResetAction = () => {
    handleResetTranscript();
    setOpenConfirmModal(false);
  };

  // <<<<<<<<<<<<<<<<<<< DRAG IMAGE >>>>>>>>>>>>>>>>>>>
  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    image: IImage,
  ) => {
    if (!image?.id) return;

    e.dataTransfer.setData('text/plain', image.id);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // <<<<<<<<<<<<<<<<<<< TranscriptEdit >>>>>>>>>>>>>>>>>>>
  const currentEditRef = useRef<{
    [key: string]: IDelta | null;
  }>({});

  const { saveAttachment } = useSession();
  const [transcriptEditIndex, setTranscriptEditIndex] = useState<number | null>(
    null,
  );

  const updateTranscriptEdits = async (
    segment: ITranscriptSegment,
    delta: IDelta,
  ) => {
    if (!segment || !booking?.booking_id) return;
    const id = `edit-${segment.time.toString()}`;
    console.log(':::updateTranscriptEdits delete id:', id);

    if (
      await saveAttachment(booking.booking_id.toString(), {
        id,
        delta: delta as IDelta,
      })
    ) {
      (transcriptAttachments[id] ??= []).push(delta);

      console.log(
        ':::updateTranscriptEdits transcriptAttachments[id]:',
        transcriptAttachments[id],
      );

      setTranscriptEditIndex(null);
    }
  };

  // <<<<<<<<<<<<<<<<<<< EDIT NOTES >>>>>>>>>>>>>>>>>>>
  const [clickedNoteKey, setClickedNoteKey] = useState<string>('');
  const editingNoteRef = useRef<{
    [key: string]: string | null;
  }>({});
  const handleAddNote = (key: string) => {
    console.log('handleAddNote...', transcriptAttachments[key]);
    if (!key) return;

    const list = (transcriptAttachments[key] ??= []);
    const deletedItem = list.find(
      (item: INote | IDelta) => (item as INote)?.src === 'd',
    );
    if (!deletedItem) {
      list.push({
        title: 'Image Note',
        description: '',
        idx: list.length,
        src: 'u',
      });
    } else {
      (deletedItem as INote).src = 'u';
    }

    setClickedNoteKey(`new-${key}`);
  };

  const handleSaveNote = async (
    key: string,
    title: string,
    description: string,
    idx: number,
  ) => {
    if (!booking?.booking_id) return;

    const note = {
      description,
      title,
      idx,
      src: 'u',
    };

    console.log(':::handleSaveNote:', note, key, title, description, idx);
    if (
      await saveAttachment(booking.booking_id.toString(), {
        note,
        id: key,
      })
    ) {
      (transcriptAttachments[key] ??= [])[note.idx] = note;
    }
  };

  const deleteNote = async (key: string, idx: number) => {
    const note = {
      title: '',
      description: '',
      idx,
      src: 'd',
    };

    if (
      !booking?.booking_id ||
      !(await saveAttachment(booking.booking_id.toString(), {
        note,
        id: key,
      }))
    )
      return;

    transcriptAttachments[key][idx] = note;
    setClickedNoteKey(`deleted-${key}`);
  };

  // <<<<<<<<<<<<<<<<<<< DRAG AND DROP NOTES >>>>>>>>>>>>>>>>>>>
  const [dragging, setDragging] = useState(false);
  const handleMouseMove = (event: any) => {
    if (!dragging) return;
  };

  const handleMouseUp = () => {
    if (dragging) {
      setDragging(false);
      setTimeout(() => {
        setDragging(false);
      }, 100);
    }
  };

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  const handleNoteDragStart = (
    e: DragEvent<HTMLDivElement>,
    key: string,
    idx: number,
  ) => {
    console.log(':::handleNoteDragStart key:', key);
    e.dataTransfer.setData('text/plain', JSON.stringify({ key, idx }));
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDropNote = async (
    e: DragEvent<HTMLDivElement>,
    dropKey: string,
  ) => {
    e.preventDefault();
    if (!booking?.booking_id) return;

    const { key, idx } = JSON.parse(e.dataTransfer.getData('text/plain'));
    const _note = transcriptAttachments[key][idx] as INote;
    if (!_note) return;

    const newNote = { ..._note, idx: transcriptAttachments[dropKey].length };

    if (
      await saveAttachment(booking.booking_id.toString(), {
        note: newNote,
        id: dropKey,
      })
    ) {
      (transcriptAttachments[dropKey] ??= []).push(newNote);
    }

    const note: INote = {
      title: '',
      description: '',
      idx: _note.idx,
      src: 'd',
    };
    if (
      await saveAttachment(booking.booking_id.toString(), {
        note,
        id: key,
      })
    ) {
      transcriptAttachments[key][_note.idx] = note;
    }

    setClickedNoteKey(`drop-${dropKey}`);
  };

  // <<<<<<<<<<<<<<<<<<< ANALYZE IMAGE >>>>>>>>>>>>>>>>>>>
  const [analyzeSelectedImage, setAnalyzeImageSelected] =
    useState<IImage | null>(null);

  const addPrompt = (image: IImage) => {
    setAnalyzeImageSelected(image);
  };

  const handleCancel = () => {
    setAnalyzeImageSelected(null);
  };

  // <<<<<<<<<<<<<<<<<<< TRACE OUTPUTS >>>>>>>>>>>>>>>>>>>
  const scrollToSegment = (index: number) => {
    const segmentElement = document.getElementById(`_segment-${index}`);
    if (segmentElement) {
      segmentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    return () => {
      setHighlightedSids([]);
    };
  }, []);

  const getSentenceStyle = (
    sentenceId: string,
  ): {
    color: string | undefined;
    score: number | undefined;
    backgroundColor: string | undefined;
  } => {
    let score;
    let backgroundColor;
    let color = highlightedSids.length > 0 ? '#ccc' : 'inherit';

    if (copilotEnabled) {
      if (aiMode === 'Filter' && filterTranscriptPrompt) {
        color = '#ccc';
        score = filterTranscriptResultsMap.get(sentenceId);

        if (score && score > scoreThreshold) {
          color = 'inherit';
        } else if (hideFiltedText)
          return { color: undefined, score, backgroundColor };
      } else {
        const class_index = Number(
          classifyTranscriptResultsMapRef?.current?.get(sentenceId),
        );

        color =
          selectedClassifierOptions.length === 0 ||
          selectedClassifierOptions.includes(class_index)
            ? 'inherit'
            : '#ccc';
        if (color === '#ccc' && hideFiltedText)
          return { color: undefined, score, backgroundColor };
      }
      if (selectedSentenceId === sentenceId) color = '#0c0';
    } else if (highlightedSids.includes(sentenceId)) {
      color = '#000';
      backgroundColor = '#FFFACD';
    }

    return { color, score, backgroundColor };
  };

  useEffect(() => {
    console.log(':::::highlightedSids:', highlightedSids);

    //put back to previous settings
    setHideImages(highlightedSids.length > 0);
    setHideNotes(highlightedSids.length > 0);
    setHideAIResults(highlightedSids.length > 0);

    if (highlightedSids.length > 0) {
      setTimeout(() => {
        const firstHighlightedElement = document.querySelector(
          `[data-sid="${highlightedSids[0]}"]`,
        );

        if (firstHighlightedElement) {
          firstHighlightedElement.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
          });
        }
      }, 100);
    }
  }, [highlightedSids]);

  //add scroll to
  useEffect(() => {
    if (
      transcriptEditIndex !== null ||
      Object.keys(editingNoteRef.current || {}).length > 0
    )
      return;

    if (!scrollTo) {
      scrollToBottom(scrollableElRef);
    } else {
      const element = document.getElementById(scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [transcriptSegments]);

  const download = (fname: string, blob: any) => {
    console.log(`download: ${fname}`);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `${fname}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log(`done: ${fname}`);
  };

  const handleDownloadTranscript = async () => {
    console.log('::::handleDownloadTranscript');
    try {
      const response =
        (await downloadSessionData?.({
          bookingId: selectedBooking.booking_id,
          raw: true,
        })) ?? '';

      if (response.status === 'success') {
        if (response?.data?.length) {
          download(
            `transcript_${selectedBooking.booking_session_code}.json`,
            new Blob([response.data], { type: 'text/plain' }),
          );
          toggleToast(
            'Transcript has been saved to your "Download" folder',
            'success',
          );
        } else {
          toggleToast('Transcript is empty', 'error');
        }
      }
    } catch (error) {
      console.log('ERROR', error);
      toggleToast('Failed to download transcript', 'error');
    }
  };

  const iconTray = (
    <Box
      sx={{
        ...styles.iconTray,
        borderBottom: useTabs
          ? 'none'
          : `0.0625rem solid ${theme.palette.divider}`,

        backgroundColor: useTabs ? 'transparent' : '#fff',
      }}>
      <IncludeOptions
        hasImages={!!imagesMapRef?.current?.size}
        hasNotes={!!Object.keys(transcriptAttachments || {}).length}
        hideImages={hideImages}
        setHideImages={setHideImages}
        hideNotes={hideNotes}
        setHideNotes={setHideNotes}
        hideAIResults={hideAIResults}
        setHideAIResults={setHideAIResults}
        hideFiltedText={
          copilotEnabled && aiMode === 'Filter' ? hideFiltedText : null
        }
        setHideFiltedText={setHideFiltedText}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        setRegenerateIndex={setRegenerateIndex}
        isGeneratingResponse={isGeneratingResponse}
        handleDownloadTranscript={handleDownloadTranscript}
        toggleCollapseAll={toggleCollapseAll}
        handleConfirmReset={handleConfirmReset}
        allCollapsed={allCollapsed}
        userSettings={userSettings}
        transcriptSegments={transcriptSegments}
        setOpenUploadModal={setOpenUploadModal}
        styles={styles}
      />
      {callObject && (
        <FormControlLabel
          sx={{ ml: 0, mr: 0 }}
          control={
            <HPSwitch
              onClick={() =>
                setTranscribeState(transcribeState === 'on' ? 'stop' : 'start')
              }
              checked={transcribeState === 'on'}
              disabled={
                transcribeState === 'starting' ||
                transcribeState === 'stopping' ||
                (remoteParticipant && remoteParticipant['owner'])
              }
            />
          }
          label="Transcribe"
          labelPlacement="start"
        />
      )}
    </Box>
  );

  return (
    <>
      <UploadDialog
        open={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        handleUpload={handleUpload}
        handleFileChange={handleFileChange}
      />

      <ConfirmResetDialog
        open={openConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmResetAction}
      />

      <AnalyzeDialog
        open={!!analyzeSelectedImage}
        onClose={handleCancel}
        analyzeSelectedImage={analyzeSelectedImage}
        booking={booking}
      />

      {useTabs && (
        <StyledSideTabs value={tabValue} onChange={handleChangeTab}>
          <StyledSideTab value={0} label={viewTitle} />
          {iconTray}
        </StyledSideTabs>
      )}

      <Box
        height={'100%'}
        sx={{
          height: '100%',
          backgroundColor: '#fff',
        }}>
        {!useTabs && iconTray}
        <Box ref={scrollableElRef} sx={styles.transcriptsPanel}>
          {transcriptSegments?.map(
            (segment: ITranscriptSegment, index: number) => {
              const image = segment?.image_id
                ? imagesMapRef?.current?.get(segment.image_id)
                : null;

              const editKey = `edit-${segment.time}`;
              const noteKey = `note-${
                !image ? segment.time : new Date(image?.date || '').getTime()
              }`;
              const notes = transcriptAttachments[noteKey] as INote[];

              return segment?.sentences ? (
                <Box
                  key={`segment-${index}`}
                  id={`_segment-${index}`}
                  sx={styles.transcriptBlock}>
                  <Box sx={styles.transcriptBlockHeader}>
                    <Typography
                      fontSize="0.813rem"
                      fontWeight={600}
                      color={highlightedSids.length > 0 ? '#eee' : 'inherit'}>
                      {`${
                        profile?.sub === segment.user_id
                          ? 'You'
                          : !segment.user_name
                            ? segment.user_id
                            : segment.user_name.split(' ')[0]
                      }:`}
                    </Typography>
                    <Typography
                      sx={styles.displayTime}
                      color={
                        highlightedSids.length > 0 ? '#eee' : 'textSecondary'
                      }>
                      {segment.displayTime ? segment.displayTime : ''}
                    </Typography>
                  </Box>
                  <Box sx={styles.transcriptSegment}>
                    {transcriptEditIndex === index ? (
                      <EditBox
                        key={editKey}
                        editKey={editKey}
                        segment={segment}
                        styles={styles}
                        setTranscriptEditIndex={setTranscriptEditIndex}
                        transcriptAttachments={transcriptAttachments}
                        updateTranscriptEdits={updateTranscriptEdits}
                        currentEditRef={currentEditRef}
                      />
                    ) : !!transcriptAttachments[editKey] &&
                      highlightedSids.length === 0 ? (
                      <EditorWrapper sx={styles.transcriptBlockText}>
                        {renderOps(
                          transcriptAttachments[editKey]?.[
                            transcriptAttachments[editKey]?.length - 1
                          ] as IDelta,
                        )}
                      </EditorWrapper>
                    ) : (
                      <Typography sx={styles.transcriptBlockText}>
                        {segment.sentences.map(
                          (sentence: ITranscriptSentence, sIndex) => {
                            const { color, score, backgroundColor } =
                              getSentenceStyle(sentence.sid);

                            if (color === null) return <></>;

                            return (
                              <StyledSentence
                                id={`${sentence.sid || `${segment.time}`}`}
                                key={sentence.sid || `sentence-${sIndex}`}
                                data-sid={sentence.sid}
                                onMouseUp={(e) => {
                                  return copilotEnabled && aiMode === 'Search'
                                    ? handleClickSentence(e)
                                    : highlightSentence
                                      ? highlightSentence(sentence.sid)
                                      : null;
                                }}
                                color={color}
                                backgroundColor={backgroundColor}
                                title={!score ? '' : `score: ${score}`}
                                hoverEnabled={
                                  (copilotEnabled && aiMode === 'Search') ||
                                  !!highlightSentence
                                }>
                                {sentence.text}
                              </StyledSentence>
                            );
                          },
                        )}
                      </Typography>
                    )}
                    {transcriptEditIndex === null &&
                      highlightedSids.length === 0 && (
                        <Box
                          sx={styles.transcriptBlockFooter}
                          className={'transcriptBlockFooter'}>
                          <IconButton
                            onClick={() => {
                              currentEditRef.current[editKey] = null;
                              setTranscriptEditIndex(index);
                            }}
                            sx={styles.hoverIcon}>
                            <BorderColor
                              sx={{ height: '1.25rem', width: '1.25rem' }}
                            />
                          </IconButton>
                          <IconButton
                            disabled={transcriptAttachments[noteKey]?.some(
                              (obj: INote | IDelta) =>
                                (obj as INote)?.src === 'u',
                            )}
                            onClick={(e) => handleAddNote(noteKey)}
                            sx={styles.hoverIcon}>
                            <PostAdd
                              sx={{ height: '1.25rem', width: '1.25rem' }}
                            />
                          </IconButton>
                        </Box>
                      )}
                  </Box>
                  {notes && (
                    <Box sx={{ margin: '.5rem 1.5rem' }}>
                      {notes.map((note: INote) =>
                        (note.src === 'u' && !hideNotes) ||
                        (note.src === 'a' && !hideAIResults) ? (
                          <TranscriptNote
                            id={note.idx}
                            note={note}
                            editingNoteRef={editingNoteRef}
                            collapsed={allCollapsed}
                            color={note.src === 'a' ? '#e0f7fa' : '#f7f6d4'}
                            saveNote={
                              note.src === 'u'
                                ? (
                                    title: string,
                                    description: string,
                                  ): void => {
                                    handleSaveNote(
                                      noteKey,
                                      title,
                                      description,
                                      note.idx,
                                    );
                                  }
                                : undefined
                            }
                            deleteNote={(e) => deleteNote(noteKey, note.idx)}
                            onDragStart={
                              note.src === 'u'
                                ? (e) => {
                                    e.stopPropagation();
                                    handleNoteDragStart(e, noteKey, note.idx);
                                  }
                                : undefined
                            }
                          />
                        ) : null,
                      )}
                    </Box>
                  )}
                </Box>
              ) : (
                image && !hideImages && (
                  <ImageBlock
                    key={`image-${index}`}
                    editingNoteRef={editingNoteRef}
                    image={image}
                    index={index}
                    noteKey={noteKey}
                    hideNotes={hideNotes}
                    collapsed={allCollapsed}
                    styles={styles}
                    handleDropNote={handleDropNote}
                    handleDragOver={handleDragOver}
                    handleDragStart={handleDragStart}
                    setSelectedImage={setSelectedImage}
                    addPrompt={addPrompt}
                    addNote={() => handleAddNote(noteKey)}
                    saveNote={(title: string, description: string) => {
                      console.log('::::saveNote: ', title, description, index);
                      handleSaveNote(noteKey, title, description, index);
                    }}
                    deleteNote={(e: any, idx: number) => {
                      e.stopPropagation();
                      console.log('::::deleteNote: ', noteKey, idx);
                      deleteNote(noteKey, idx);
                    }}
                    notes={transcriptAttachments[noteKey] as INote[]}
                  />
                )
              );
            },
          )}
        </Box>
      </Box>
    </>
  );
};

export default Transcript;
